// src/lib/api.js
// Universal fetch helper — CRA-safe (no import.meta), supports runtime + env overrides.

// ---------- API base resolution (CRA) ----------
function readMetaApiBase() {
  if (typeof document === "undefined") return null;
  const tag = document.querySelector('meta[name="api-base"]');
  const v = tag?.getAttribute("content") || tag?.dataset?.apiBase;
  return v && typeof v === "string" ? v.trim() : null;
}

function firstNonEmpty(...vals) {
  for (const v of vals) {
    if (v && String(v).trim() !== "") return String(v).trim();
  }
  return "";
}

// Precedence (highest → lowest):
// 1) window.__API_BASE (runtime override; optional)
// 2) <meta name="api-base" content="..."> (runtime; optional)
// 3) process.env.REACT_APP_API_BASE (CRA build-time; optional)
// 4) process.env.REACT_APP_BACKEND_URL (your current var; build-time)
// 5) "" (same-origin; only valid if your host rewrites /api/*)
const RUNTIME_WINDOW_BASE =
  (typeof window !== "undefined" && window.__API_BASE) || null;
const RUNTIME_META_BASE = readMetaApiBase();

export const API_BASE = firstNonEmpty(
  RUNTIME_WINDOW_BASE,
  RUNTIME_META_BASE,
  typeof process !== "undefined" &&
    process.env &&
    process.env.REACT_APP_API_BASE,
  typeof process !== "undefined" &&
    process.env &&
    process.env.REACT_APP_BACKEND_URL,
  ""
);

// Expose for quick verification in DevTools.
if (typeof window !== "undefined") window.__API_BASE = API_BASE;

// ---------- Fetch helper ----------
/**
 * apiFetch(path, {
 *   method?: string,
 *   headers?: Record<string,string>,
 *   body?: any,
 *   requireAuth?: boolean,
 *   getToken?: () => Promise<string>,
 *   timeoutMs?: number,  // default 15000
 *   signal?: AbortSignal,
 *   ...rest
 * })
 */
export async function apiFetch(path, options = {}) {
  const {
    method = "GET",
    headers = {},
    body,
    requireAuth = false,
    getToken,
    timeoutMs = 15000,
    signal: externalSignal,
    ...rest
  } = options;

  // Normalize URL (avoid accidental double slashes; preserve absolute paths)
  const url =
    API_BASE && path
      ? `${String(API_BASE).replace(/\/+$/, "")}/${String(path).replace(/^\/+/, "")}`
      : String(path);

  const finalHeaders = new Headers({
    Accept: "application/json",
    ...headers,
  });

  // Bodies we should pass through as-is
  const isSpecialBody =
    (typeof FormData !== "undefined" && body instanceof FormData) ||
    (typeof Blob !== "undefined" && body instanceof Blob) ||
    (typeof ArrayBuffer !== "undefined" && body instanceof ArrayBuffer) ||
    (typeof URLSearchParams !== "undefined" && body instanceof URLSearchParams);

  const upperMethod = String(method).toUpperCase();
  const hasBody = body !== undefined && body !== null && upperMethod !== "GET";

  if (hasBody && !finalHeaders.has("Content-Type") && !isSpecialBody && typeof body !== "string") {
    finalHeaders.set("Content-Type", "application/json");
  }

  // Attach Bearer token if requested (Firebase-compatible)
  if (requireAuth) {
    let token = null;

    if (typeof getToken === "function") {
      try { token = await getToken(); } catch {}
    }

    if (!token) {
      const w = typeof window !== "undefined" ? window : {};
      // Compat for namespaced and modular Firebase exposed on window
      const fba =
        (w.firebase && typeof w.firebase.auth === "function" && w.firebase.auth()) ||
        (w.auth && typeof w.auth === "object" ? w.auth : null) ||
        null;

      let currentUser = null;
      try {
        if (fba && fba.currentUser) currentUser = fba.currentUser;
        else if (typeof w.getAuth === "function") currentUser = w.getAuth()?.currentUser || null;
      } catch {}

      if (currentUser && typeof currentUser.getIdToken === "function") {
        try { token = await currentUser.getIdToken(true); } catch {}
      }
    }

    if (token) finalHeaders.set("Authorization", `Bearer ${token}`);
  }

  // Timeout + Abort support
  const controller = new AbortController();
  if (externalSignal) {
    if (externalSignal.aborted) controller.abort();
    else externalSignal.addEventListener("abort", () => controller.abort(), { once: true });
  }
  let timeoutId = null;
  if (Number.isFinite(timeoutMs) && timeoutMs > 0) {
    timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  }

  let requestBody;
  if (!hasBody) requestBody = undefined;
  else if (typeof body === "string" || isSpecialBody) requestBody = body;
  else requestBody = JSON.stringify(body);

  let res;
  try {
    res = await fetch(url, {
      method: upperMethod,
      headers: finalHeaders,
      body: requestBody,
      signal: controller.signal,
      ...rest,
    });
  } catch (netErr) {
    if (timeoutId) clearTimeout(timeoutId);
    const isAbort = netErr && (netErr.name === "AbortError" || netErr.code === "ABORT_ERR");
    const err = new Error(
      isAbort ? `Request aborted/timed out after ${timeoutMs}ms` : `Network error contacting ${url}`
    );
    err.cause = netErr;
    err.isApiError = true;
    err.status = 0;
    throw err;
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }

  const contentType = res.headers.get("content-type") || "";

  // Handle non-2xx with rich errors
  if (!res.ok) {
    // Helper to build a consistent error object
    const buildErr = (msg) => {
      const e = new Error(msg || `Request failed (${res.status})`);
      e.response = res;
      e.isApiError = true;
      e.status = res.status;
      return e;
    };

    if (contentType.includes("application/json")) {
      try {
        const bodyJson = await res.json();

        // Prefer the most user-friendly field for message
        const friendly =
          bodyJson?.userMessage ||
          bodyJson?.reason ||
          bodyJson?.error ||
          bodyJson?.message ||
          `Request failed (${res.status})`;

        const err = buildErr(friendly);
        // Attach structured fields for callers to branch on
        err.details = bodyJson || null;
        err.code = bodyJson?.code || null;
        err.reason = bodyJson?.reason || bodyJson?.error || null;
        err.body = bodyJson;

        throw err;
      } catch {
        // If JSON parsing itself failed, fall through to text handler below
      }
    }

    if (contentType.includes("text/html")) {
      const hint =
        API_BASE
          ? `API_BASE="${API_BASE}" may be wrong (HTML came back).`
          : `You're using same-origin ("") base; your host must rewrite /api/* to the backend.`;
      const err = buildErr(`API returned HTML for ${url}. ${hint}`);
      throw err;
    }

    let bodyText = "";
    try { bodyText = await res.text(); } catch {}
    const err = buildErr(bodyText || `Request failed (${res.status})`);
    err.bodyText = bodyText || null;
    throw err;
  }

  // Success
  if (res.status === 204) return null;

  if (contentType.includes("application/json")) {
    return res.json();
  }

  const text = await res.text();
  try {
    return JSON.parse(text);
  } catch {
    throw new Error(`Unexpected response from ${url}: ${text.slice(0, 100)}...`);
  }
}
